import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Grid,
  Tooltip,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  LocalHospital as FeverIcon,
  Favorite as HeartIcon,
  LocalDining as DiabetesIcon,
  Category as GeneralIcon,
  TrendingUp as TrendingIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { PrescriptionTemplate } from '../../types';
import { templates, templateEnhancements } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

interface TemplateSelectorDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectTemplate: (template: PrescriptionTemplate) => void;
  doctorId: string;
}

const categoryIcons: Record<string, React.ReactElement> = {
  FEVER: <FeverIcon />,
  HYPERTENSION: <HeartIcon />,
  DIABETES: <DiabetesIcon />,
  GENERAL: <GeneralIcon />,
};

// Friendly labels for known categories; unknown ones fall back to Title-case.
const CATEGORY_LABELS: Record<string, string> = {
  FEVER: 'Fever',
  HYPERTENSION: 'Hypertension',
  DIABETES: 'Diabetes',
  GENERAL: 'General',
  RESPIRATORY: 'Respiratory',
  GASTRO: 'Gastro',
  PAIN: 'Pain & MSK',
  INFECTION: 'Infection',
  SKIN: 'Skin',
  ENT: 'ENT',
  PEDIATRIC: 'Paediatric',
};

function labelForCategory(cat: string): string {
  return CATEGORY_LABELS[cat] || cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase();
}

/**
 * Fuzzy fallback for legacy template medications that have no medicationMasterId.
 */
function isMedAvailableByName(templateMedName: string, clinicMeds: any[]): boolean {
  if (!templateMedName || !clinicMeds?.length) return false;
  const tNorm = templateMedName.toLowerCase().trim();
  return clinicMeds.some((cm) => {
    const cmName = (cm.medicationName || '').toLowerCase().trim();
    const cmFull = [cm.medicationName, cm.strength].filter(Boolean).join(' ').toLowerCase().trim();
    return tNorm.includes(cmName) || tNorm.includes(cmFull) || cmFull.includes(tNorm) || cmName === tNorm;
  });
}

export const TemplateSelectorDialog: React.FC<TemplateSelectorDialogProps> = ({
  open,
  onClose,
  onSelectTemplate,
  doctorId,
}) => {
  const { userData } = useAuth();
  const clinicId = userData?.clinicId || '';

  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [templatesList, setTemplatesList] = useState<PrescriptionTemplate[]>([]);
  const [favoritesList, setFavoritesList] = useState<PrescriptionTemplate[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<PrescriptionTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);
  // Local optimistic favorite state: templateId → boolean
  const [localFavorites, setLocalFavorites] = useState<Record<string, boolean>>({});
  const [favTogglingId, setFavTogglingId] = useState<string | null>(null);

  // Category tabs are derived from the loaded templates, so any standard or doctor-created
  // category becomes filterable automatically (ALL + Favorites always shown first).
  const categories = useMemo(() => {
    const present = Array.from(
      new Set(templatesList.map((t) => t.category).filter(Boolean) as string[])
    ).sort();
    return [
      { value: 'ALL', label: 'All Templates' },
      { value: 'FAVORITES', label: '⭐ Favorites' },
      ...present.map((c) => ({ value: c, label: labelForCategory(c) })),
    ];
  }, [templatesList]);

  // Fetch all enabled clinic medications once (for fuzzy fallback on legacy templates)
  const { data: clinicMedsData } = useQuery({
    queryKey: ['clinic-meds-for-template', clinicId],
    queryFn: () => axios.get(`/api/medications/clinic/${clinicId}/available`),
    enabled: open && !!clinicId,
    select: (res) => res.data?.data || [],
    staleTime: 5 * 60 * 1000,
  });
  const clinicMeds: any[] = clinicMedsData || [];

  // Collect all unique medication_master_ids across loaded templates
  const allMasterIds = React.useMemo(() => {
    const ids = new Set<string>();
    templatesList.forEach((t) =>
      t.medications?.forEach((m: any) => {
        if (m.medicationMasterId) ids.add(m.medicationMasterId);
      })
    );
    return Array.from(ids);
  }, [templatesList]);

  // Exact-match availability via the batch check endpoint
  const { data: availabilityMap } = useQuery<Record<string, boolean>>({
    queryKey: ['template-med-availability', clinicId, allMasterIds.join(',')],
    queryFn: () =>
      axios
        .post(`/api/medications/clinic/${clinicId}/check-availability`, { medicationIds: allMasterIds })
        .then((r) => r.data?.data || {}),
    enabled: open && !!clinicId && allMasterIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
  const idAvailability: Record<string, boolean> = availabilityMap || {};

  useEffect(() => {
    if (open) {
      setSelectedCategory('ALL');
      setSearchQuery('');
      setLocalFavorites({});
      loadTemplates();
    }
  }, [open, doctorId]);

  // When switching to FAVORITES tab, load from backend
  useEffect(() => {
    if (selectedCategory === 'FAVORITES' && open) {
      loadFavorites();
    }
  }, [selectedCategory, open]);

  useEffect(() => {
    filterTemplates();
  }, [selectedCategory, searchQuery, templatesList, favoritesList]);

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await templates.list(undefined, doctorId);
      if (response.data) {
        setTemplatesList(response.data);
        // Seed localFavorites from whatever field the backend may return
        const favMap: Record<string, boolean> = {};
        response.data.forEach((t: any) => {
          if (t.isFavorite) favMap[t.id] = true;
        });
        setLocalFavorites(favMap);
      } else {
        setError(response.error || 'Failed to load templates');
      }
    } catch {
      setError('An error occurred while loading templates');
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    setFavoritesLoading(true);
    try {
      const response = await templateEnhancements.getFavorites();
      if (response.data) {
        setFavoritesList(response.data);
        // Sync into localFavorites
        setLocalFavorites((prev) => {
          const next = { ...prev };
          response.data!.forEach((t: any) => { next[t.id] = true; });
          return next;
        });
      }
    } catch {
      // Non-fatal — favorites tab shows empty
    } finally {
      setFavoritesLoading(false);
    }
  };

  const filterTemplates = () => {
    const source = selectedCategory === 'FAVORITES' ? favoritesList : templatesList;
    let filtered = source;

    if (selectedCategory !== 'ALL' && selectedCategory !== 'FAVORITES') {
      filtered = filtered.filter((t) => t.category === selectedCategory);
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.name.toLowerCase().includes(query) ||
          t.diagnosis?.toLowerCase().includes(query) ||
          t.chiefComplaint?.toLowerCase().includes(query)
      );
    }
    filtered.sort((a, b) => b.useCount - a.useCount);
    setFilteredTemplates(filtered);
  };

  const handleUseTemplate = useCallback(async (template: PrescriptionTemplate) => {
    onSelectTemplate(template);
    onClose();
    // Record usage in background — fire and forget
    templateEnhancements.recordUsage(template.id).catch(() => {});
  }, [onSelectTemplate, onClose]);

  const handleToggleFavorite = async (e: React.MouseEvent, template: PrescriptionTemplate) => {
    e.stopPropagation();
    const nowFavorite = !localFavorites[template.id];
    // Optimistic update
    setLocalFavorites((prev) => ({ ...prev, [template.id]: nowFavorite }));
    setFavTogglingId(template.id);
    try {
      await templateEnhancements.toggleFavorite(template.id, nowFavorite);
      if (!nowFavorite && selectedCategory === 'FAVORITES') {
        // Remove from local favorites list
        setFavoritesList((prev) => prev.filter((t) => t.id !== template.id));
      } else if (nowFavorite && selectedCategory === 'FAVORITES') {
        // Refresh favorites list to include newly added
        loadFavorites();
      }
    } catch {
      // Revert on failure
      setLocalFavorites((prev) => ({ ...prev, [template.id]: !nowFavorite }));
    } finally {
      setFavTogglingId(null);
    }
  };

  const toggleExpanded = (templateId: string) => {
    setExpandedTemplate(expandedTemplate === templateId ? null : templateId);
  };

  const isMedAvailable = (med: any): boolean => {
    if (med.medicationMasterId) {
      return idAvailability[med.medicationMasterId] === true;
    }
    return isMedAvailableByName(med.medicationName, clinicMeds);
  };

  const getTemplateMedAvailability = (template: PrescriptionTemplate) => {
    if (!template.medications?.length) return null;
    if (!clinicMeds.length && Object.keys(idAvailability).length === 0) return null;
    const unavailableCount = template.medications.filter((m) => !isMedAvailable(m)).length;
    return { total: template.medications.length, unavailable: unavailableCount };
  };

  const isLoadingCurrent = selectedCategory === 'FAVORITES' ? favoritesLoading : loading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Select Prescription Template</Typography>
          <Chip
            icon={<TrendingIcon />}
            label={`${filteredTemplates.length} templates`}
            color="primary"
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <TextField
          fullWidth
          placeholder="Search templates by name, diagnosis, or complaint..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        <Tabs
          value={selectedCategory}
          onChange={(_, newValue) => setSelectedCategory(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {categories.map((cat) => (
            <Tab key={cat.value} label={cat.label} value={cat.value} />
          ))}
        </Tabs>

        {isLoadingCurrent && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!isLoadingCurrent && !error && (
          <Grid container spacing={2}>
            {filteredTemplates.length === 0 ? (
              <Grid item xs={12}>
                <Alert severity="info">
                  {selectedCategory === 'FAVORITES'
                    ? 'No favourites yet — star a template to add it here.'
                    : `No templates found.${selectedCategory !== 'ALL' ? ' Try a different category or' : ''} ${searchQuery ? 'Try a different search term.' : ''}`}
                </Alert>
              </Grid>
            ) : (
              filteredTemplates.map((template) => {
                const availability = getTemplateMedAvailability(template);
                const hasUnavailable = availability && availability.unavailable > 0;
                const hasAvailabilityData = clinicMeds.length > 0 || Object.keys(idAvailability).length > 0;
                const isFav = !!localFavorites[template.id];
                const isToggling = favTogglingId === template.id;

                return (
                  <Grid item xs={12} key={template.id}>
                    <Card variant="outlined" sx={hasUnavailable ? { borderColor: 'warning.main' } : {}}>
                      <CardContent>
                        <Box display="flex" alignItems="start" justifyContent="space-between" mb={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {categoryIcons[template.category] || <GeneralIcon />}
                            <Typography variant="h6">{template.name}</Typography>
                          </Box>
                          <Box display="flex" gap={0.5} alignItems="center">
                            <Tooltip title={isFav ? 'Remove from favourites' : 'Add to favourites'}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleToggleFavorite(e, template)}
                                  disabled={isToggling}
                                  color={isFav ? 'warning' : 'default'}
                                  sx={{ p: 0.5 }}
                                >
                                  {isToggling
                                    ? <CircularProgress size={16} />
                                    : isFav
                                      ? <StarIcon fontSize="small" />
                                      : <StarBorderIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Chip
                              label={template.category}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            {template.useCount > 0 && (
                              <Tooltip title="Times used">
                                <Chip
                                  icon={<TrendingIcon />}
                                  label={template.useCount}
                                  size="small"
                                  color="success"
                                />
                              </Tooltip>
                            )}
                            {availability && hasAvailabilityData && (
                              <Tooltip title={
                                hasUnavailable
                                  ? `${availability.unavailable} of ${availability.total} medications not assigned to this clinic`
                                  : 'All medications available at this clinic'
                              }>
                                <Chip
                                  icon={hasUnavailable
                                    ? <WarningIcon style={{ fontSize: 14 }} />
                                    : <CheckCircleIcon style={{ fontSize: 14 }} />
                                  }
                                  label={hasUnavailable
                                    ? `${availability.total - availability.unavailable}/${availability.total} available`
                                    : 'All available'
                                  }
                                  size="small"
                                  color={hasUnavailable ? 'warning' : 'success'}
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </Box>

                        {template.chiefComplaint && (
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            <strong>Chief Complaint:</strong> {template.chiefComplaint}
                          </Typography>
                        )}

                        {template.diagnosis && (
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            <strong>Diagnosis:</strong> {template.diagnosis}
                          </Typography>
                        )}

                        {expandedTemplate === template.id && (
                          <Box mt={2}>
                            {template.clinicalNotes && (
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                <strong>Clinical Notes: </strong> {template.clinicalNotes}
                              </Typography>
                            )}

                            {template.medications && template.medications.length > 0 && (
                              <Box mt={1}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Medications ({template.medications.length}):
                                </Typography>
                                {template.medications.map((med: any, idx: number) => {
                                  const available = isMedAvailable(med);
                                  return (
                                    <Box key={idx} pl={2} mb={0.5} display="flex" alignItems="flex-start" gap={0.5}>
                                      <Tooltip title={
                                        !hasAvailabilityData ? ''
                                          : available ? 'Available at this clinic' : 'Not assigned to this clinic'
                                      }>
                                        <Box component="span" sx={{ mt: 0.3, flexShrink: 0 }}>
                                          {hasAvailabilityData && (
                                            available
                                              ? <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                              : <WarningIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                                          )}
                                        </Box>
                                      </Tooltip>
                                      <Box>
                                        <Typography variant="body2"
                                          sx={{ color: (!available && hasAvailabilityData) ? 'warning.dark' : 'inherit' }}>
                                          {idx + 1}. <strong>{med.medicationName}</strong>
                                          {med.dosage && ` - ${med.dosage}`}
                                          {med.frequency && ` - ${med.frequency}`}
                                          {med.duration && ` for ${med.duration}`}
                                        </Typography>
                                        {med.instructions && (
                                          <Typography variant="caption" color="text.secondary" display="block" pl={1}>
                                            {med.instructions}
                                          </Typography>
                                        )}
                                        {!available && hasAvailabilityData && (
                                          <Typography variant="caption" color="warning.main" display="block" pl={1}>
                                            Not in clinic's medication list — you can still use it
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}

                            {hasUnavailable && (
                              <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
                                {availability!.unavailable} medication{availability!.unavailable > 1 ? 's are' : ' is'} not
                                assigned to your clinic. You can still use this template — the prescription will be saved as-is.
                              </Alert>
                            )}
                          </Box>
                        )}
                      </CardContent>

                      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                        <Button
                          size="small"
                          startIcon={<ViewIcon />}
                          onClick={() => toggleExpanded(template.id)}
                        >
                          {expandedTemplate === template.id ? 'Show Less' : 'View Details'}
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          color={hasUnavailable ? 'warning' : 'primary'}
                          onClick={() => handleUseTemplate(template)}
                        >
                          Use Template
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })
            )}
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};
